import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import { VENUE_TYPE_CONFIG, DEFAULT_VENUE_TYPE } from "../../../lib/consts/venueTypes";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import Button from "../../Elements/Button";

function fmt(n) { return Number(n || 0).toFixed(2); }
function fmtGr(n) { return Number(n || 0).toFixed(3); }
// Fecha local del navegador — NO toISOString(): en Madrid entre las 00:00 y las
// 02:00 la fecha UTC aún es "ayer" y los presets darían el día equivocado.
function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function today() { return new Date().toISOString().slice(0, 10); }
function oneMonthAgo() {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const p = (x) => String(x).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Las dos "zonas" no son configurables: son los dos lados del flag `is_dispensario`
// de cada producto. En un club que no dispensa cannabis (abacería, bistró) NINGÚN
// producto lo lleva, así que la pestaña del dispensario está vacía por construcción
// — y encima se llamaba "Dispensario Cannábico" en una abacería de alimentos
// (reporte del cliente, CRUO 18-ago: "lo de cannábico no es lo suyo jejeje").
//
// Se decide por el mismo `showDispenser` que ya gobierna el resto de la app, para
// que no haya dos verdades sobre si este club dispensa o no.
const ZONA_DISPENSARIO = { id: "dispensario", label: "Dispensario Cannábico" };
const ZONA_BARRA = { id: "barra", label: "Bebida / comida social" };

export function zonasDe(venueType) {
  const cfg = VENUE_TYPE_CONFIG[venueType] || VENUE_TYPE_CONFIG[DEFAULT_VENUE_TYPE];
  return cfg?.showDispenser ? [ZONA_DISPENSARIO, ZONA_BARRA] : [ZONA_BARRA];
}

const DETAIL_TABS = [
  { id: "aportaciones", label: "Aportaciones" },
  { id: "produccion", label: "Producción" },
  { id: "devolucion", label: "Devolución" },
];

// Columnas de la tabla. `numeric` => orden por número; si no, alfabético (Genética).
const COLUMNS = [
  { key: "name", label: "Genética", numeric: false },  // ver `columnasDe`: en un club
  // sin dispensario esta columna se llama "Producto" (una abacería no tiene genéticas).
  { key: "gr_aportados", label: "GR aportados", numeric: true },
  { key: "crd_aportados", label: "CRD aportados", numeric: true },
  { key: "crd_gr_aportacion", label: "CRD/GR", numeric: true },
  { key: "gr_produccion", label: "GR producción", numeric: true },
  { key: "crd_produccion", label: "CRD producción", numeric: true },
  { key: "rendimiento", label: "Rendimiento", numeric: true },
];

/** Las columnas, con la primera renombrada donde "Genética" no significa nada. */
export function columnasDe(venueType) {
  const cfg = VENUE_TYPE_CONFIG[venueType] || VENUE_TYPE_CONFIG[DEFAULT_VENUE_TYPE];
  if (cfg?.showDispenser) return COLUMNS;
  return COLUMNS.map((c) => (c.key === "name" ? { ...c, label: "Producto" } : c));
}

/** Cantidad aportada: unidades para fijos, gr para peso; debajo, cuántos socios
 *  DISTINTOS la aportaron (petición de Kong, 14-ago — antes salía el nº de líneas
 *  de venta disfrazado de "und", que no era ni unidades ni socios). */
function qtyLabel(row) {
  const socios = row.socios_aportacion || 0;
  const principal =
    row.unit_type === "fixed"
      ? `${Math.round(row.gr_aportados || 0)} und.`
      : `${fmtGr(row.gr_aportados)} gr.`;
  return (
    <span>
      {principal}
      {socios > 0 && (
        <span className="block text-xs italic text-gray-500">
          {socios} {socios === 1 ? "socio" : "socios"}
        </span>
      )}
    </span>
  );
}

export default function ContabilidadGeneral({ token, venueID, beforeZones = null }) {
  const venueType = useContext(UserContext)[11];
  const zonas = useMemo(() => zonasDe(venueType), [venueType]);
  const columnas = useMemo(() => columnasDe(venueType), [venueType]);
  // Arrancar en la primera zona DISPONIBLE: en una abacería, "dispensario" pedía al
  // servidor un cajón vacío por construcción y el panel abría en blanco.
  const [zone, setZone] = useState(() => zonasDe(venueType)[0].id);
  // Cambiar de club en la cabecera no puede dejar seleccionada una zona que este
  // club no tiene: se quedaría pidiendo "dispensario" en una abacería.
  useEffect(() => {
    if (!zonas.some((z) => z.id === zone)) setZone(zonas[0].id);
  }, [zonas, zone]);
  const [fromDate, setFromDate] = useState(oneMonthAgo);
  const [toDate, setToDate] = useState(today);
  // Horas opcionales (petición Kong: "de madrugada a madrugada"). Vacío = día completo.
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Orden de la tabla (por defecto: más gramos aportados primero)
  const [sortKey, setSortKey] = useState("gr_aportados");
  const [sortDir, setSortDir] = useState("desc");

  const onSort = useCallback((col) => {
    setSortKey((prevKey) => {
      if (prevKey === col.key) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        return prevKey;
      }
      // Nueva columna: numéricas empiezan en desc (más → menos); texto en asc.
      setSortDir(col.numeric ? "desc" : "asc");
      return col.key;
    });
  }, []);

  // Modal de detalle por genética
  const [detailRow, setDetailRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("aportaciones");
  const [detailLoading, setDetailLoading] = useState(false);

  // Acepta fechas explícitas para los presets: setState es asíncrono y el fetch
  // inmediato leería el rango viejo del closure.
  const fetchData = useCallback(async (desde, hasta, horaDesde, horaHasta) => {
    if (!token || !venueID) return;
    const f = typeof desde === "string" ? desde : fromDate;
    const t = typeof hasta === "string" ? hasta : toDate;
    const hf = typeof horaDesde === "string" ? horaDesde : fromTime;
    const ht = typeof horaHasta === "string" ? horaHasta : toTime;
    const horas = `${hf ? `&from_time=${hf}` : ""}${ht ? `&to_time=${ht}` : ""}`;
    setLoading(true); setError("");
    try {
      const res = await fetch(
        `/v1/reports/contabilidad-genetica?venue_id=${venueID}&zone=${zone}&from_date=${f}&to_date=${t}${horas}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al cargar la contabilidad");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, zone, fromDate, toDate, fromTime, toTime]);

  // Recarga al montar y al cambiar de zona
  useEffect(() => { fetchData(); }, [zone]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailTab("aportaciones");
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(
        `/v1/reports/contabilidad-genetica/${row.product_id}?venue_id=${venueID}&from_date=${fromDate}&to_date=${toDate}${fromTime ? `&from_time=${fromTime}` : ""}${toTime ? `&to_time=${toTime}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al cargar el detalle");
      setDetail(await res.json());
    } catch {
      setDetail({ aportaciones: [], produccion: [], devolucion: [] });
    } finally {
      setDetailLoading(false);
    }
  }, [token, venueID, fromDate, toDate, fromTime, toTime]);

  const rows = useMemo(() => {
    const base = data?.rows || [];
    const col = columnas.find((c) => c.key === sortKey) || columnas[1];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      if (col.numeric) {
        return (Number(a[col.key] || 0) - Number(b[col.key] || 0)) * dir;
      }
      return String(a[col.key] || "").localeCompare(String(b[col.key] || ""), "es", { sensitivity: "base" }) * dir;
    });
  }, [data, sortKey, sortDir, columnas]);

  return (
    <div className="space-y-4">
      {/* Zona (+ slot opcional a la izquierda, p.ej. el conmutador Gráficas/Balance) */}
      <div className="flex flex-wrap items-center gap-3">
        {beforeZones}
        {zonas.length > 1 && (
        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {zonas.map((z) => (
            <button
              key={z.id}
              onClick={() => setZone(z.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                zone === z.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Filtro de fechas + balance */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        {/* Presets: un toque y sin tocar fechas a mano — el resbalón de "julio"
            (14-ago: el club consultó 07/07-14/07 y creyó que no había datos). */}
        <div className="flex flex-wrap gap-1">
          {[
            { label: "Últimos 7 días", dias: 7 },
            { label: "Últimos 30 días", dias: 30 },
            // Días cerrados: hasta AYER, sin el día en curso a medias (petición Kong 16-ago)
            { label: "7 días completos", dias: 7, hastaAyer: true },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                const h = new Date();
                if (p.hastaAyer) h.setDate(h.getDate() - 1);
                const hasta = ymdLocal(h);
                const d = new Date(); d.setDate(d.getDate() - p.dias);
                const desde = ymdLocal(d);
                setFromDate(desde); setToDate(hasta);
                setFromTime(""); setToTime("");
                fetchData(desde, hasta, "", "");
              }}
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50"
              title={p.hastaAyer ? "Días cerrados: no incluye el día de hoy" : undefined}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">Desde</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Hora desde (opcional)"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">hasta</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Hora hasta (opcional)"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
          />
        </div>
        <Button onClick={fetchData} disabled={loading}>
          {loading ? "Cargando…" : "Ver Balance"}
        </Button>
        {data && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-2 rounded-lg">
              Flujo contable {fmt(data.flujo_contable)} Crd
            </div>
            <div className="bg-gray-900 text-white text-sm font-bold px-4 py-2 rounded-lg">
              BALANCE TOTAL = {fmt(data.balance_total)} Crd
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {loading && <div className="text-center py-16 text-gray-400 text-sm">Cargando datos…</div>}

      {!loading && data && (
        <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--card)]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <TableHead>
                {columnas.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <Th key={col.key} align={col.numeric ? "right" : "left"}>
                      <button
                        type="button"
                        onClick={() => onSort(col)}
                        className="inline-flex items-center gap-1 transition-colors hover:text-[var(--primary)]"
                        title="Ordenar"
                      >
                        <span>{col.label}</span>
                        <span className={active ? "text-[var(--primary)]" : "text-[var(--muted-foreground)]"}>
                          {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </Th>
                  );
                })}
              </TableHead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><Td align="center" colSpan={7} className="py-10 text-sm text-[var(--muted-foreground)]">Sin datos en el período</Td></tr>
                ) : (
                  rows.map((row) => (
                    <Tr
                      key={row.product_id}
                      onClick={() => openDetail(row)}
                      className="cursor-pointer"
                    >
                      <Td className="font-medium text-[var(--foreground)]">{row.name}</Td>
                      <Td align="right">{qtyLabel(row)}</Td>
                      <Td align="right">{fmt(row.crd_aportados)} Crd</Td>
                      <Td align="right" className="text-[var(--muted-foreground)]">{fmt(row.crd_gr_aportacion)} Crd/gr.</Td>
                      <Td align="right">{fmtGr(row.gr_produccion)} gr.</Td>
                      <Td align="right">{fmt(row.crd_produccion)} Crd</Td>
                      <Td align="right" className={`font-semibold ${row.rendimiento < 0 ? "text-[var(--destructive)]" : "text-[var(--foreground)]"}`}>
                        {fmt(row.rendimiento)} Crd
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de detalle por genética */}
      {detailRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-bold text-gray-800">Genética {detailRow.name}</h2>
              <button
                onClick={() => setDetailRow(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                aria-label="Cerrar"
              >×</button>
            </div>

            <div className="flex gap-1 border-b border-gray-200 px-5 pt-3">
              {DETAIL_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setDetailTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    detailTab === t.id
                      ? "bg-[var(--muted)] text-[var(--foreground)] border-b-2 border-[var(--primary)]"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {detailLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Cargando…</div>
              ) : detailTab === "produccion" ? (
                <DetailTable
                  cols={["Concepto", "Gramos", "Importe", "Fecha"]}
                  rows={(detail?.produccion || []).map((r) => [
                    r.concepto,
                    `${fmtGr(r.gramos)} gr.`,
                    `${fmt(r.importe)} Crd`,
                    fmtDate(r.fecha),
                  ])}
                  empty="Sin entradas de producción en el período"
                />
              ) : (
                <DetailTable
                  cols={["Socio", "Socio Nº", "Gramos", "Importe", "Fecha"]}
                  rows={((detailTab === "devolucion" ? detail?.devolucion : detail?.aportaciones) || []).map((r) => [
                    r.socio,
                    r.socio_num ?? "—",
                    `${fmtGr(r.gramos)} gr.`,
                    `${fmt(r.importe)} Crd`,
                    fmtDate(r.fecha),
                  ])}
                  empty={detailTab === "devolucion" ? "Sin devoluciones en el período" : "Sin aportaciones en el período"}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailTable({ cols, rows, empty }) {
  return (
    <table className="w-full text-sm">
      {/* Celdas compactas a propósito: cabe dentro del modal de detalle. */}
      <TableHead>
        {cols.map((c, i) => (
          <Th key={c} align={i === 0 ? "left" : "right"} className="px-3 py-2">{c}</Th>
        ))}
      </TableHead>
      <tbody>
        {rows.length === 0 ? (
          <tr><Td align="center" colSpan={cols.length} className="py-8 text-sm text-[var(--muted-foreground)]">{empty}</Td></tr>
        ) : (
          rows.map((r, ri) => (
            <Tr key={ri}>
              {r.map((cell, ci) => (
                <Td key={ci} align={ci === 0 ? "left" : "right"} className={`px-3 py-2 ${ci === 0 ? "font-medium" : ""}`}>
                  {cell}
                </Td>
              ))}
            </Tr>
          ))
        )}
      </tbody>
    </table>
  );
}
